/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    async init() {
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form');
      this.personaliser = this.section.querySelector('.shopify-block[id*="zepto"]');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.nativeSelector = document.getElementById(`variants-${this.section.dataset.section}`);
      this.data = await this.getVariantData();
      this.selectedOptions = this.getSelectedOptions(true);
      this.variant = this.getSelectedVariant();
      this.quantityInput = this.closest('.product-form')?.querySelector('#quantity');

      this.updateAvailability();
      this.updateAddToCartButton();
      this.addEventListener('change', this.handleVariantChange.bind(this));
      this.quantityInput?.addEventListener('change', this.handleVariantChange.bind(this));

      this.setAttribute('loaded', '');

      const preSelectedOptions = JSON.parse(sessionStorage.getItem('htp-preselected-values') || "[]");
      if(preSelectedOptions.length) {
        this.optionSelectors.forEach(selector=>{
          if(selector.dataset.selectorType === "dropdown") return;
          const label = selector.dataset.label;
          if(!label) return;
          const preselectedValue = preSelectedOptions.find(opt=>opt[0]===label)?.[1];
          if(preselectedValue !== undefined || preselectedValue!== null) {
            const matchingInput = selector.querySelector(`input[value="${preselectedValue}"]`);
            const matchingInputId = matchingInput?.id;
            const isAvailable = !matchingInput?.classList.contains('is-unavailable');
            if(matchingInputId && isAvailable) {
              selector.querySelector(`label[for="${matchingInputId}"]`)?.click();
            } else {
              this.getSelectedOptions();
            }
          }
        })
      }
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.updateVariantInput();
      this.updateUrl(evt);
      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateQuantityInput(evt);
      this.updateBackorderText();
      this.updateBackorderProperty();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateBarcode();
      VariantPicker.updateLabelText(evt);

      if (this.personaliser) {
        this.personaliser.classList.toggle('hidden', !(this.variant && this.variant.available));
      }

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          allVariants: this.data.variants,
          selectedOptions: this.selectedOptions
        }
      }));
    }

    /**
     * Updates the "Add to Cart" button label and status.
     */
    updateAddToCartButton() {
      if (!this.productForm || this.selectedOptions.indexOf(null) > -1) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;

      const quantityRow = this.productForm.querySelector('.quantity-row');
      if (quantityRow) quantityRow.hidden = !variantAvailable;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMode } = this.dataset; // 'down' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMode === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    updateQuantityInput(evt) {
      if (!this.quantityInput) return;

      const quantityUp = this.quantityInput.parentElement.querySelector('[data-quantity=up]');
      const quantityDown = this.quantityInput.parentElement.querySelector('[data-quantity=down]');
      const inventory_quantity = this.variant ? this.data.formatted[this.variant.id]?.inventory_quantity : 0;
      const inventory_policy = this.variant ? this.data.formatted[this.variant.id]?.inventory_policy : 'deny';
      const isVariantChange = !evt || evt.target !== this.quantityInput;
      const minQuantity = this.variant?.available ? 1 : 0;
      const limitedByStock = Boolean(this.variant?.inventory_management) && !isNaN(inventory_quantity) && inventory_policy === 'deny';

      let quantity = isVariantChange ? minQuantity : Number(this.quantityInput.value);
      if (isNaN(quantity) || quantity < minQuantity) quantity = minQuantity;
      if (limitedByStock && quantity > inventory_quantity) quantity = Math.max(inventory_quantity, minQuantity);

      this.quantityInput.value = quantity;
      quantityUp?.toggleAttribute('data-disabled', !this.variant?.available || (limitedByStock && quantity >= inventory_quantity));
      quantityDown?.toggleAttribute('data-disabled', quantity <= Math.max(minQuantity, 1));
    }

		updateBackorderProperty() {
      const quantity = Number(this.quantityInput?.value || 1);
			this.backorderAttrWrap = this.section.querySelector('.backorder-attr-wrap');
      this.backorderMessageWrap = this.section.querySelector('.backorder-message-wrap');
			if (!this.backorderAttrWrap || !this.backorderMessageWrap) return;
      let backorderInput = this.backorderAttrWrap.querySelector('#backorder');
      const generalMessage = this.backorderMessageWrap.getAttribute('data-general-message');
      const backorderMessage = this.backorderMessageWrap.getAttribute('data-backorder-message');
      const partialBackorderMessage = this.backorderMessageWrap.getAttribute('data-partial-backorder-message');
			let hideBackorderAttr = true;

      const inventory_quantity = this.variant ? this.data.formatted[this.variant.id]?.inventory_quantity : 0;
      const inventory_policy = this.variant ? this.data.formatted[this.variant.id]?.inventory_policy : 'deny';

			if (this.variant?.available && this.variant.inventory_management && quantity > inventory_quantity && inventory_policy === 'continue') {
					hideBackorderAttr = false;
					if (!backorderInput) {
						backorderInput = document.createElement('input');
						backorderInput.type = 'hidden';
						backorderInput.id = 'backorder';
						backorderInput.name = 'properties[_backorder]';
						this.backorderAttrWrap.appendChild(backorderInput);
					}
					backorderInput.value = inventory_quantity <= 0 ? backorderMessage : partialBackorderMessage.replace('[items_quantity]', inventory_quantity);
			}

      if (this.variant?.available) {
        if (hideBackorderAttr) {
          backorderInput?.remove();
          this.backorderMessageWrap.innerText = generalMessage;
        } else {
          this.backorderMessageWrap.innerText = inventory_quantity <= 0 ? backorderMessage : partialBackorderMessage.replace('[items_quantity]', inventory_quantity);
        }
      } else {
        backorderInput?.remove();
        this.backorderMessageWrap.innerText = '';
      }
		}

		/**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      const quantity = Number(this.quantityInput?.value || 1);
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      const inventory_quantity = this.variant ? this.data.formatted[this.variant.id]?.inventory_quantity : 0;
      const inventory_policy = this.variant ? this.data.formatted[this.variant.id]?.inventory_policy : 'deny';

      if (this.variant?.available && this.variant?.inventory_management && quantity > inventory_quantity && inventory_policy === 'continue') {
        const backorderProdEl = this.backorder.querySelector('.backorder__product');
        const prodTitleEl = this.section.querySelector('.product-title');
        const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;

        backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
        hideBackorder = false;
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the colour option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (!selector) return;
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-price .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.variants[0];
        for (let i = 1; i < this.data.variants.length; i += 1) {
          if (this.data.variants[i].price < variant.price) variant = this.data.variants[i];
        }
      }

      if (variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = `€${this.data.formatted[variant.id].price}`;
        if (priceWasEl) {
          const priceWas = this.data.formatted[variant.id].compareAtPrice || ''
          priceWasEl.innerHTML = `${priceWas ? '€' : ''}${priceWas}`;
        }

        // Update unit price, if specified.
        if (variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        this.price.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
        this.price.classList.toggle('price--sold-out', !variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcode = this.barcode || this.section.querySelector('.product-barcode__value');
      if (!this.barcode) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
      this.barcode.parentNode.hidden = !barcodeAvailable;
    }

    /**
     * Updates the url with the selected variant id, preserving the active ?view= template.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      let url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;

      // Keep the alternate template (e.g. ?view=meta5) on the url, otherwise a reload or a shared
      // link drops back to the default product template.
      const view = new URLSearchParams(window.location.search).get('view');
      if (view) {
        url += `${url.includes('?') ? '&' : '?'}view=${encodeURIComponent(view)}`;
      }

      window.history.replaceState({ }, '', url);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms = this.forms || this.section.querySelectorAll('.js-product-form, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        input.value = this.variant ? this.variant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    async getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]');
      const data = JSON.parse(dataEl.textContent);
      const variants_count = data.variants_count;
      if (!variants_count || !data.product_handle) return {};

      if (variants_count > 250) {
        const extra_pages = Math.ceil((variants_count - 250) / 50);
        let fetchUrls = [];

        for (let i = 1; i <= extra_pages; i++) {
          fetchUrls.push(`/products/${data.product_handle}/?page=${i + 5}&view=paginated-variants`);
        }

        const paginatedVariantsData = await Promise.all(fetchUrls.map((url) => fetch(url).then((res) => res.ok ? res.json() : null)));
        paginatedVariantsData.forEach((variantsData) => {
          if (!variantsData) return;
          data.variants = [...data.variants, ...variantsData.variants];
          data.formatted = {...data.formatted, ...variantsData.formatted};
        });
      }

      return data;
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions(firstLoad) {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const label = selector.dataset.label;
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
          if(!label) return;
          let preSelectedOptions = JSON.parse(sessionStorage.getItem('htp-preselected-values') || "[]");
          if(selected) {
            let existingSelection = preSelectedOptions.find(opt=>opt[0]===label);
            if(existingSelection) {
              if(!firstLoad) existingSelection[1] = selected.value;
            } else {
              preSelectedOptions.push([label,selected.value]);
            }
          }
          sessionStorage.setItem('htp-preselected-values',JSON.stringify(preSelectedOptions));
        }
      });

      return selectedOptions;
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      return this.data.variants.find(
        (v) => v.options.every((val, index) => val === this.selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
